import { Component, Input, Output, EventEmitter, forwardRef, HostBinding, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { IconComponent } from '../icon';

export type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'checkbox'
  | 'radio';

export type MaskType =
  | 'cpf'
  | 'cnpj'
  | 'phone'
  | 'cep'
  | 'currency'
  | 'numeric'
  | 'none';

export type InputSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  template: `
    <div class="w-full">
      <!-- Checkbox/Radio Layout (inline label) -->
      <div
        *ngIf="type === 'checkbox' || type === 'radio'"
        class="flex items-start gap-2"
      >
        <input
          [id]="inputId"
          [type]="type"
          [disabled]="disabled"
          [required]="required"
          [(ngModel)]="value"
          (blur)="onBlur()"
          (change)="onCheckboxChange($event)"
          [ngClass]="getCheckboxRadioClasses()"
          [attr.aria-label]="ariaLabel || label"
          [attr.aria-describedby]="
            error || hint ? inputId + '-description' : null
          "
          [attr.aria-invalid]="error ? 'true' : null"
        />
        <label
          *ngIf="label"
          [for]="inputId"
          class="text-sm text-gray-700 cursor-pointer select-none"
          [ngClass]="{ 'opacity-60': disabled }"
        >
          {{ label }}
          <span *ngIf="required" class="text-red-500 ml-1">*</span>
        </label>
      </div>

      <!-- Standard Input Layout (label above) -->
      <ng-container *ngIf="type !== 'checkbox' && type !== 'radio'">
        <!-- Label -->
        <label
          *ngIf="label"
          [for]="inputId"
          class="block text-sm font-medium text-gray-700 mb-2"
        >
          {{ label }}
          <span *ngIf="required" class="text-red-500">*</span>
        </label>

        <!-- Input Container -->
        <div class="relative">
          <!-- Left Icon -->
          <div
            *ngIf="leftIcon"
            class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          >
            <app-icon
              [name]="leftIcon"
              [className]="'w-5 h-5 text-gray-400'"
            ></app-icon>
          </div>

          <!-- Input -->
          <input
            [id]="inputId"
            [type]="type"
            [placeholder]="placeholder"
            [disabled]="disabled"
            [readonly]="readonly"
            [required]="required"
            [min]="min"
            [max]="max"
            [step]="step"
            [maxlength]="maxlength"
            [pattern]="pattern"
            [autocomplete]="autocomplete"
            [(ngModel)]="value"
            (blur)="onBlur()"
            (input)="onInput($event)"
            [ngClass]="getInputClasses()"
            [attr.aria-label]="ariaLabel || label"
            [attr.aria-describedby]="
              error || hint ? inputId + '-description' : null
            "
            [attr.aria-invalid]="error ? 'true' : null"
          />

          <!-- Right Icon -->
          <div
            *ngIf="rightIcon"
            class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          >
            <app-icon
              [name]="rightIcon"
              [className]="'w-5 h-5 text-gray-400'"
            ></app-icon>
          </div>

          <!-- Clear Button (for search type) -->
          <button
            *ngIf="type === 'search' && value && !disabled"
            type="button"
            (click)="clearInput()"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <app-icon name="x" className="w-4 h-4"></app-icon>
          </button>
        </div>
      </ng-container>

      <!-- Helper Text / Error -->
      <div
        *ngIf="error || hint"
        [id]="inputId + '-description'"
        class="mt-1 text-sm"
        [ngClass]="{
          'text-red-500': error,
          'text-gray-500': !error && hint,
          'ml-6': type === 'checkbox' || type === 'radio'
        }"
      >
        {{ error || hint }}
      </div>
    </div>
  `,
})
export class InputComponent implements ControlValueAccessor, OnInit {
  @Input() label?: string;
  @Input() type: InputType = 'text';
  @Input() mask: MaskType = 'none';
  @Input() placeholder: string = '';
  @Input() hint?: string;
  @Input() error?: string;
  @Input() leftIcon?: string;
  @Input() rightIcon?: string;
  @Input() size: InputSize = 'md';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() autocomplete?: string;
  @Input() min?: string | number;
  @Input() max?: string | number;
  @Input() step?: string | number;
  @Input() maxlength?: number;
  @Input() pattern?: string;
  @Input() ariaLabel?: string;
  @Input() inputId: string = `input-${Math.random().toString(36).substr(2, 9)}`;
  @Input() className: string = '';

  @Output() blur = new EventEmitter<void>();

  value: any = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  // Auto-configure based on mask type
  ngOnInit(): void {
    this.applyMaskConfiguration();
  }

  private applyMaskConfiguration(): void {
    if (this.mask === 'none') return;

    switch (this.mask) {
      case 'cpf':
        this.maxlength = 14; // xxx.xxx.xxx-xx
        this.placeholder = this.placeholder || '000.000.000-00';
        break;
      case 'cnpj':
        this.maxlength = 18; // xx.xxx.xxx/xxxx-xx
        this.placeholder = this.placeholder || '00.000.000/0000-00';
        break;
      case 'phone':
        this.maxlength = 15; // (xx) xxxxx-xxxx
        this.placeholder = this.placeholder || '(00) 00000-0000';
        break;
      case 'cep':
        this.maxlength = 9; // xxxxx-xxx
        this.placeholder = this.placeholder || '00000-000';
        break;
      case 'currency':
        this.placeholder = this.placeholder || 'R$ 0,00';
        break;
    }
  }

  writeValue(value: any): void {
    if (this.mask === 'numeric') {
      // For numeric mask, ensure only numbers
      this.value = value ? value.toString().replace(/\D/g, '') : '';
    } else if (this.mask !== 'none' && value) {
      this.value = this.applyMask(value);
    } else {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let rawValue = input.value;

    if (this.mask === 'numeric') {
      // For numeric mask, only allow numbers and respect maxlength
      const numericValue = rawValue.replace(/\D/g, '');
      this.value = numericValue;
      input.value = numericValue;
      this.onChange(numericValue);
    } else if (this.mask !== 'none') {
      // Remove all non-numeric characters for mask processing
      const numericValue = rawValue.replace(/\D/g, '');

      // Apply mask
      const maskedValue = this.applyMask(numericValue);
      this.value = maskedValue;

      // Update input display
      input.value = maskedValue;

      // Emit raw numeric value for form control
      this.onChange(numericValue);
    } else {
      this.value = rawValue;
      this.onChange(this.value);
    }
  }

  private applyMask(value: string): string {
    if (!value || this.mask === 'none') return value;

    const numericValue = value.replace(/\D/g, '');

    switch (this.mask) {
      case 'cpf':
        return this.formatCPF(numericValue);
      case 'cnpj':
        return this.formatCNPJ(numericValue);
      case 'phone':
        return this.formatPhone(numericValue);
      case 'cep':
        return this.formatCEP(numericValue);
      case 'currency':
        return this.formatCurrency(numericValue);
      default:
        return value;
    }
  }

  private formatCPF(value: string): string {
    // Format: xxx.xxx.xxx-xx
    if (value.length <= 3) return value;
    if (value.length <= 6) return `${value.slice(0, 3)}.${value.slice(3)}`;
    if (value.length <= 9) return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6)}`;
    return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6, 9)}-${value.slice(9, 11)}`;
  }

  private formatCNPJ(value: string): string {
    // Format: xx.xxx.xxx/xxxx-xx
    if (value.length <= 2) return value;
    if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
    if (value.length <= 8) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
    if (value.length <= 12) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8)}`;
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
  }

  private formatPhone(value: string): string {
    // Format: (xx) xxxxx-xxxx or (xx) xxxx-xxxx
    if (value.length <= 2) return value.length === 2 ? `(${value})` : value;
    if (value.length <= 7) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length <= 10) return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
    return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
  }

  private formatCEP(value: string): string {
    // Format: xxxxx-xxx
    if (value.length <= 5) return value;
    return `${value.slice(0, 5)}-${value.slice(5, 8)}`;
  }

  private formatCurrency(value: string): string {
    // Format: R$ 1.234,56
    if (!value) return 'R$ 0,00';

    const numericValue = parseInt(value) || 0;
    const formattedValue = (numericValue / 100).toFixed(2);
    const [integer, decimal] = formattedValue.split('.');

    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `R$ ${formattedInteger},${decimal}`;
  }

  onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  onCheckboxChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = this.type === 'checkbox' ? input.checked : input.value;
    this.onChange(this.value);
  }

  clearInput(): void {
    this.value = '';
    this.onChange(this.value);
  }

  getCheckboxRadioClasses(): string {
    const baseClasses =
      this.type === 'checkbox'
        ? 'h-4 w-4 rounded border-gray-300 text-[#4A90E2] focus:ring-[#4A90E2] focus:ring-2 focus:ring-offset-0 transition-colors cursor-pointer mt-0.5'
        : 'h-4 w-4 border-gray-300 text-[#4A90E2] focus:ring-[#4A90E2] focus:ring-2 focus:ring-offset-0 transition-colors cursor-pointer mt-0.5';

    const disabledClasses = this.disabled
      ? 'opacity-60 cursor-not-allowed'
      : '';

    return `${baseClasses} ${disabledClasses} ${this.className}`.trim();
  }

  getInputClasses(): string {
    const baseClasses =
      'w-full border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:border-transparent';

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const stateClasses = this.error
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-[#4A90E2] focus:ring-[#4A90E2]';

    const disabledClasses = this.disabled
      ? 'bg-gray-100 cursor-not-allowed opacity-60'
      : 'bg-white';

    const readonlyClasses = this.readonly ? 'bg-gray-50 cursor-default' : '';

    const iconPadding = {
      left: this.leftIcon ? 'pl-10' : '',
      right:
        this.rightIcon || (this.type === 'search' && this.value) ? 'pr-10' : '',
    };

    return `${baseClasses} ${
      sizeClasses[this.size]
    } ${stateClasses} ${disabledClasses} ${readonlyClasses} ${
      iconPadding.left
    } ${iconPadding.right} ${this.className}`.trim();
  }
}
